'use client'

import Image from 'next/image'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChevronsUpDown } from 'lucide-react'
import { cn } from '@/utils/styles/cn'
import { CHANNELS_TABS } from '@/features/landing-page/v3/sections/channels-section-data'
import { LP_GRID } from '@/features/landing-page/v3/lp-grid'

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

/**
 * lp-v3 "Superscale brings agentic power to your …" section. A chalk panel with a tab bar and a
 * centre dropdown (kept in sync) that switch between Channels / Integrations / Agents-MCP over a row
 * of capability cards. CLIENT + interactive, but EVERY tab's subtitle and cards ship in the static
 * HTML (stacked, cross-faded) so all content stays crawlable for SEO. Only Channels is real content;
 * Integrations / Agents-MCP are mocked. `!` overrides the `.ds2` typography.
 */
export function LpChannelsSection() {
    const [activeId, setActiveId] = useState(CHANNELS_TABS[0].id)
    const [open, setOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const active = CHANNELS_TABS.find((tab) => tab.id === activeId) ?? CHANNELS_TABS[0]

    useEffect(() => {
        if (!open) return
        const onPointerDown = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', onPointerDown)
        return () => document.removeEventListener('mousedown', onPointerDown)
    }, [open])

    return (
        <section className={`bg-background ${LP_GRID.cardSection} py-[60px] md:py-[40px]`}>
            {/* Widescreen: the card BACKGROUND fills the gutter width; the content inside keeps its
                own max width so it never stretches on large screens. The card fills one viewport
                (stageMinH); the inner column flexes to fill it so the elastic middle can distribute. */}
            <div
                className={`rounded-[24px] bg-[#F0EEEA] md:flex md:flex-col md:rounded-[32px] ${LP_GRID.cardPad} ${LP_GRID.stageMinH}`}
            >
                <div className={`${LP_GRID.content} md:flex md:flex-1 md:flex-col`}>
                {/* Tab bar — Label style; active is orange. All labels ship in the HTML (SEO). */}
                <div className="flex items-center justify-center gap-7">
                    {CHANNELS_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveId(tab.id)}
                            className={cn(
                                'text-[10px]! font-bold uppercase leading-[1.04]! tracking-[0.04em] transition-colors',
                                tab.id === activeId ? 'text-[#FF5500]!' : 'text-[#A8A097]! hover:text-foreground!'
                            )}
                        >
                            {tab.tabLabel}
                        </button>
                    ))}
                </div>

                {/* Centre block — the elastic middle (Rule 6): flex-1 absorbs all the stage's height
                    growth and keeps the heading + dropdown + subtitle optically centred. */}
                <div className="flex flex-col items-center py-14 text-center md:flex-1 md:justify-center md:py-12">
                    <h2>
                        <span className="block text-[28px]! font-normal leading-[1.1]! text-foreground md:text-[40px]!">
                            Superscale brings
                        </span>
                        <span className="block text-[28px]! leading-[1.1]! md:text-[40px]!">
                            <span className="font-[family-name:var(--font-landing-pixel)]! font-bold text-[#FF5500]!">
                                agentic power
                            </span>
                            <span className="font-normal text-foreground"> to your</span>
                        </span>
                    </h2>

                    {/* Dropdown — mirrors / drives the tab bar; menu opens centred over the trigger */}
                    <div ref={dropdownRef} className="relative mt-7">
                        <button
                            type="button"
                            onClick={() => setOpen((value) => !value)}
                            aria-haspopup="listbox"
                            aria-expanded={open}
                            className="inline-flex items-center gap-2 rounded-full bg-[#131210] px-5 py-2.5 text-[16px]! font-normal leading-[1.25]! tracking-[-0.02em]! text-[#FDFBF8]! outline-none focus-visible:ring-2 focus-visible:ring-[#FF5500]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#F0EEEA]"
                        >
                            <AnimatedTriggerLabel label={active.dropdownLabel} />
                            <ChevronsUpDown className="size-4 shrink-0 text-[#FDFBF8]" />
                        </button>

                        <div
                            role="listbox"
                            className={cn(
                                'absolute left-1/2 top-1/2 z-30 w-[220px] -translate-x-1/2 -translate-y-1/2 rounded-[20px] bg-[#131210] p-8 shadow-[0_20px_50px_rgba(0,0,0,0.25)]',
                                'origin-center transition-[opacity,transform,scale] duration-200 ease-out',
                                open ? 'scale-100 opacity-100' : 'pointer-events-none scale-95 opacity-0'
                            )}
                        >
                            <div className="flex flex-col gap-5 text-center">
                                {CHANNELS_TABS.map((tab) => (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        onClick={() => {
                                            setActiveId(tab.id)
                                            setOpen(false)
                                        }}
                                        className={cn(
                                            'text-[24px]! font-normal leading-[1]! tracking-[-0.02em]! transition-colors',
                                            tab.id === activeId ? 'text-white!' : 'text-[#A8A097]! hover:text-white!'
                                        )}
                                    >
                                        {tab.dropdownLabel}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Subtitle — one per tab, cross-faded; all in the HTML (SEO) */}
                    <div className="relative mt-5 w-full">
                        {CHANNELS_TABS.map((tab) => (
                            <p
                                key={tab.id}
                                aria-hidden={tab.id !== activeId}
                                className={cn(
                                    'text-[16px]! font-normal leading-[1.25]! tracking-[-0.02em]! text-[#A8A097]! transition-opacity duration-300',
                                    tab.id === activeId
                                        ? 'relative opacity-100'
                                        : 'pointer-events-none absolute inset-x-0 top-0 opacity-0'
                                )}
                            >
                                {tab.subtitle}
                            </p>
                        ))}
                    </div>
                </div>

                {/* Capability cards — one grid per tab, cross-faded; all in the HTML (SEO) */}
                <div className="relative border-t border-black/[0.08]">
                    {CHANNELS_TABS.map((tab) => (
                        <div
                            key={tab.id}
                            aria-hidden={tab.id !== activeId}
                            className={cn(
                                'grid grid-cols-1 transition-opacity duration-300 md:grid-cols-4',
                                tab.id === activeId
                                    ? 'relative opacity-100'
                                    : 'pointer-events-none absolute inset-x-0 top-0 opacity-0'
                            )}
                        >
                            {tab.cards.map((card, index) => (
                                <div
                                    key={card.title}
                                    className={cn(
                                        'flex items-center gap-4 py-5 md:h-[190px] md:flex-col md:items-start md:justify-between md:gap-0 md:px-6 md:py-8',
                                        index > 0 && 'border-t border-black/[0.08] md:border-l md:border-t-0'
                                    )}
                                >
                                    <div className="flex shrink-0 items-center gap-1.5">
                                        {card.icons.map((icon) => (
                                            <span
                                                key={icon}
                                                className="flex size-10 items-center justify-center rounded-[10px] border border-[#D7D2C9]"
                                            >
                                                <Image
                                                    src={icon}
                                                    alt=""
                                                    width={22}
                                                    height={22}
                                                    className="size-[22px] object-contain"
                                                />
                                            </span>
                                        ))}
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <span className="text-[16px]! font-normal leading-[1.25]! tracking-[-0.02em]! text-foreground">
                                            {card.title}
                                        </span>
                                        {card.subtitle && (
                                            <span className="text-[16px]! font-normal leading-[1.25]! tracking-[-0.02em]! text-[#A8A097]!">
                                                {card.subtitle}
                                            </span>
                                        )}
                                        {card.comingSoon && (
                                            <span className="mt-1 inline-flex w-fit items-center rounded-[5px] bg-black/[0.06] px-2 py-1 text-[10px]! font-bold uppercase leading-[1.04]! tracking-[0.04em] text-[#A8A097]!">
                                                Coming soon
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                </div>
            </div>
        </section>
    )
}

/**
 * The dropdown trigger's label. Cross-fades the text and smoothly animates its own width so the pill
 * resizes as the label changes (Channels → Integrations → Agents-MCP). Before mount / with JS off the
 * in-flow measurer sizes it naturally, so it renders correctly for SSR.
 */
function AnimatedTriggerLabel({ label }: { label: string }) {
    const [width, setWidth] = useState<number>()
    const measureRef = useRef<HTMLSpanElement>(null)

    useIsomorphicLayoutEffect(() => {
        if (measureRef.current) setWidth(measureRef.current.offsetWidth)
    }, [label])

    return (
        <span
            className="relative inline-block overflow-hidden whitespace-nowrap transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ width }}
        >
            <span ref={measureRef} className="invisible">
                {label}
            </span>
            <span key={label} className="lp-fade-in absolute inset-0">
                {label}
            </span>
        </span>
    )
}
